// 導演確認陣形（Cue 庫專案）：Cue 庫的每個 cue 是「已確認」或「待確認」。
// 後台規則在 cf-worker/cueConfirm.mjs：確認之後 cue 的內容被改（存回 Cue 庫、改機關位置或動作時間…）會自動退回待確認（returned = true）。
// 這裡只放前端的文字和算法（純函式，有測試）；舊模式專案 / 舊版後台沒有 confirm → 一律當「沒有這個功能」（不顯示任何標記）。

/** 後台給的確認狀態（人名只有成員看得到，唯讀連結的訪客沒有） */
export interface CueConfirm {
  status: 'confirmed' | 'pending';
  /** true = 確認之後內容被改過，自動退回待確認（畫面要標明是因為改過才退回） */
  returned: boolean;
  /** 最近一次確認的時間（ISO）；從沒確認過 = null */
  at: string | null;
  byName?: string | null;
}

export type ConfirmKind = 'confirmed' | 'pending' | 'returned';

export const CONFIRM_TEXT = {
  confirmed: '已確認',
  pending: '待確認',
  returned: '改過，已退回待確認',
  confirmAction: '標為已確認',
  returnAction: '退回待確認',
  onlyPending: '只看待確認',
  allCues: '看全部',
  progress: (done: number, total: number) => `已確認 ${done} / ${total}`,
  allDone: '全部都已確認',
  noPending: '沒有待確認的 cue',
  confirmedToast: (label: string) => `cue ${label} 已確認`,
  returnedToast: (label: string) => `cue ${label} 退回待確認`,
  staleConflict: '這個 cue 剛剛被改過，已重新載入，請看過最新的再確認',
  pendingHint: '導演還沒確認這個 cue',
  returnedHint: '確認之後這個 cue 的內容被改過，所以退回待確認；導演再看過、再確認一次才算定案',
  readOnlyHint: '只有導演或製作可以標確認',
  animatorNote: '導演確認過的才是定案，待確認的可能還會調整',
} as const;

export function confirmKind(c: { confirm?: CueConfirm | null } | null | undefined): ConfirmKind | null {
  const v = c?.confirm;
  if (!v) return null;
  if (v.status === 'confirmed') return 'confirmed';
  return v.returned ? 'returned' : 'pending';
}

export function confirmLabel(kind: ConfirmKind): string {
  return kind === 'confirmed' ? CONFIRM_TEXT.confirmed : kind === 'returned' ? CONFIRM_TEXT.returned : CONFIRM_TEXT.pending;
}

/** 「11/3 14:20」（本機時間） */
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 滑鼠停上去的說明：誰、什麼時候確認的；退回的寫原因 */
export function confirmTitle(c: { confirm?: CueConfirm | null } | null | undefined): string {
  const kind = confirmKind(c);
  const v = c?.confirm;
  if (!kind || !v) return '';
  if (kind === 'confirmed') {
    const who = v.byName ? `${v.byName} ` : '';
    const when = v.at ? shortTime(v.at) : '';
    return `${CONFIRM_TEXT.confirmed}${who || when ? `（${who}${when}）` : ''}`;
  }
  if (kind === 'returned') return `${CONFIRM_TEXT.returned}：${CONFIRM_TEXT.returnedHint}`;
  return CONFIRM_TEXT.pendingHint;
}

/** 進度：已確認幾個、總共幾個（沒有 confirm 欄位的 cue 不算，舊後台 = 0 / 0） */
export function confirmProgress(list: readonly { confirm?: CueConfirm | null }[]): { done: number; total: number; pending: number } {
  let done = 0;
  let total = 0;
  for (const c of list) {
    if (!c.confirm) continue;
    total++;
    if (c.confirm.status === 'confirmed') done++;
  }
  return { done, total, pending: total - done };
}

/** 只看待確認（含改過退回的） */
export function pendingOnly<T extends { confirm?: CueConfirm | null }>(list: readonly T[]): T[] {
  return list.filter(c => c.confirm && c.confirm.status !== 'confirmed');
}

/** 歌裡的 cue（libraryCue 帶 confirm）：這首歌確認了幾個 */
export function songConfirmProgress(cues: readonly { libraryCue?: { confirm?: CueConfirm | null } | null }[]): { done: number; total: number; pending: number } {
  return confirmProgress(cues.map(c => ({ confirm: c.libraryCue?.confirm ?? null })));
}
